import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import TermsConsent from "../../components/TermsConsent";
import { analytics, EVENTS } from "../../analytics";

const PRODUCT_LABELS: Record<string, string> = {
  "resume-kit": "영국 이력서 템플릿",
  "interview-guide-student": "인터뷰 가이드 (학생/워홀)",
  "interview-guide-experienced": "인터뷰 가이드 (경력직)",
  "linkedin-guide": "LinkedIn 최적화 가이드",
  "digital-bundle": "All-in-One 영국 취업 키트",
};

type FormState = "idle" | "loading" | "success" | "error";

const DownloadFormPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const product = params.get("product") ?? "";
  const productLabel = PRODUCT_LABELS[product] ?? product;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [formState, setFormState] = useState<FormState>("idle");
  const [errors, setErrors] = useState<{ name?: string; email?: string; agreed?: string }>({});

  const validate = () => {
    const e: typeof errors = {};
    if (!name.trim()) e.name = "이름을 입력해주세요.";
    if (!email.trim()) e.email = "이메일을 입력해주세요.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = "올바른 이메일 형식을 입력해주세요.";
    if (!agreed) e.agreed = "이용약관에 동의해주세요.";
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setFormState("loading");

    try {
      const res = await fetch("/api/send-download-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), product }),
      });

      if (!res.ok) throw new Error("server error");
      analytics.track(EVENTS.PURCHASE, { product, label: productLabel });
      setFormState("success");
    } catch {
      setFormState("error");
    }
  };

  if (formState === "success") {
    return (
      <div className="min-h-screen bg-[#F5F6F7] flex items-center justify-center px-6 py-20">
        <div className="bg-white rounded-2xl shadow-sm max-w-md w-full p-10 text-center">
          <div className="w-14 h-14 bg-[#0A1A3E] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={28} className="text-gold" />
          </div>
          <h1 className="text-xl font-extrabold text-[#0A1A3E] mb-3">
            이메일을 확인해주세요
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-2">
            <strong className="text-[#0A1A3E]">{email}</strong>로<br />
            다운로드 파일을 보내드렸습니다.
          </p>
          <p className="text-gray-400 text-xs mb-8">
            스팸함도 확인해보세요. 5분 이내에 도착하지 않으면{" "}
            <a href="mailto:info@bemeconsulting.com" className="underline text-[#0A1A3E]">
              info@bemeconsulting.com
            </a>
            으로 연락해주세요.
          </p>
          <button
            onClick={() => navigate("/resources")}
            className="text-sm text-[#0A1A3E] underline underline-offset-2"
          >
            자료 페이지로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F7] flex items-center justify-center px-6 py-20">
      <Helmet>
        <title>다운로드 정보 입력 — BeMe Consulting</title>
      </Helmet>

      <div className="bg-white rounded-2xl shadow-sm max-w-md w-full p-8 md:p-10">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs font-bold tracking-widest text-[#d4a017] uppercase mb-2">
            Purchase Complete
          </p>
          <h1 className="text-xl font-extrabold text-[#0A1A3E] mb-1">
            결제가 완료되었습니다!
          </h1>
          {productLabel && (
            <p className="text-sm text-gray-400">
              구매 상품:{" "}
              <span className="text-[#0A1A3E] font-semibold">{productLabel}</span>
            </p>
          )}
        </div>

        <p className="text-sm text-gray-500 leading-relaxed text-center mb-8">
          아래 정보를 입력하시면 주문해주신 파일을 이메일로 바로 보내드립니다.
        </p>

        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-[#0A1A3E] mb-1.5" htmlFor="name">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="홍길동"
              className={`w-full border rounded-xl px-4 py-3 text-sm text-[#0A1A3E] placeholder-gray-300 outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 transition ${
                errors.name ? "border-red-400" : "border-gray-200"
              }`}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-[#0A1A3E] mb-1.5" htmlFor="email">
              이메일 <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              className={`w-full border rounded-xl px-4 py-3 text-sm text-[#0A1A3E] placeholder-gray-300 outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 transition ${
                errors.email ? "border-red-400" : "border-gray-200"
              }`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* T&C */}
          <TermsConsent
            checked={agreed}
            onChange={setAgreed}
            error={errors.agreed}
          />

          {/* Submit */}
          <button
            type="submit"
            disabled={formState === "loading"}
            className="w-full bg-[#0A1A3E] text-white font-bold py-3.5 rounded-full text-sm hover:bg-[#305485] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {formState === "loading" ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                전송 중...
              </>
            ) : (
              "BeMe 서비스 자료 받기"
            )}
          </button>

          {formState === "error" && (
            <p className="text-red-500 text-xs text-center">
              오류가 발생했습니다. 잠시 후 다시 시도하거나{" "}
              <a href="mailto:info@bemeconsulting.com" className="underline">
                info@bemeconsulting.com
              </a>
              으로 연락해주세요.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default DownloadFormPage;
